import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import { useGoogleLogin } from "@react-oauth/google";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { bindActionCreators } from "redux";
import SocialLoginButton from "../../../components/onboarding/SocialLoginButton/SocialLoginButton";
import LazyGoogleOAuthProvider from "../../../components/shared/LazyGoogleOAuthProvider/LazyGoogleOAuthProvider";
import * as config from "../../../config/config";
import {
  convertBase64,
  getItem,
  setItem,
} from "../../../helpers/data_management";
import { posthogSignup } from "../../../helpers/posthog_management";
import { triggerSnackBar } from "../../../helpers/snackbar_management";
import {
  sendTrackingEvent,
  signupTracking,
} from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  updateCurrentSalaryDialog,
  updateJobAlertVisitorEmailDialog,
  updateResumeConfirmationDialog,
} from "../../../redux/actions/dialog_action";
import {
  hideSnackBar,
  showSnackBar,
} from "../../../redux/actions/snackbar_action";
import {
  globalLoginFunc,
  newResumeUpload,
  socialMediaLogin,
} from "../../../redux/actions/user_action";
import { store } from "../../../redux/stores/store";
import * as types from "../../../redux/types/user_type";
import SignUp from "../../onboarding/SignUp/SignUp";
import ForgotPassword from "../ForgotPassword/ForgotPassword";
import {
  ActionLabelStyled,
  CircularProgressStyled,
  CloseIconContainer,
  CustomDivider,
  CustomDividerContainer,
  DialogContentStyled,
  EmailContainer,
  EmailLoginFormContainer,
  ForgotPasswordContainer,
  FormTitle,
  GoogleButton,
  HeaderDialog,
  HiredlyLogoGrid,
  LabelDivider,
  LabelStyled,
  LinkedInButton,
  LogInButton,
  LoginDialog,
  LogoStyled,
  PasswordContainer,
  SSOLoginContainer,
  SignUpContainer,
  SocialSpinnerContainer,
  SubmissionContainer,
  TextInput,
  VisibilityIconStyled,
  VisibilityOffIconStyled,
} from "./styles";

let socialButtonRefs = {};

function TextInputFunction(props) {
  const {
    label,
    handleChange,
    type,
    email,
    password,
    onKeyDown,
    passwordShown,
    togglePassword,
    loggingIn,
  } = props;

  return (
    <Fragment>
      <TextInput
        disabled={loggingIn}
        data-testid={type == "email" ? "login-email-input" : "login-password-input"}
        label={type == "email" ? "Email" : "Password"}
        value={type == "email" ? email : password}
        type={`${type}`}
        onChange={handleChange}
        id={`filled-required-${label}`}
        variant="filled"
        onKeyDown={onKeyDown}
        password_shown={passwordShown ? "true" : "false"}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position="start"
              style={{ display: `${type == "email" ? "none" : "flex"}` }}
            >
              {passwordShown ? (
                <VisibilityIconStyled onClick={togglePassword} />
              ) : (
                <VisibilityOffIconStyled onClick={togglePassword} />
              )}
            </InputAdornment>
          ),
        }}
      />
    </Fragment>
  );
}

function getAppId(ssoLogin) {
  switch (ssoLogin) {
    case "Continue with Google": {
      return process.env.GOOGLE_CLIENT_ID;
    }
    case "Continue with Linkedin": {
      return process.env.LINKEDIN_ID;
    }
    case "Continue with Facebook": {
      return process.env.FACEBOOK_APP_ID;
    }
  }
}

// Need to pass down redux action props
function LoginForm(props) {
  const {
    handleClickSignUp,
    loggingIn,
    handleOpenForgotPassword,
    updateJobAlertVisitorEmailDialog,
    newResumeUpload,
  } = props;
  const router = useRouter();
  const [passwordShown, setPasswordShown] = useState(false);

  // For Google login
  const [hideGoogleButton, setHideGoogleButton] = useState(false);

  const user = useSelector((state) => state.user?.user);
  const fypTab = Number(useSelector((state) => state.jobs.fypTab));
  const signUpOrigin = useSelector((state) => state?.navbar?.signUpOrigin);
  const selectedJob = useSelector((state) => state?.jobs?.job);
  const selectedJobId = useSelector((state) => state.jobs?.selectedJobId);
  const dispatch = useDispatch();
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const fromDropResume = router.pathname.includes("drop-resume");
  const s3Url = Cookies.get("s3fileUrl");

  const onFailure = (response) => {
    if (
      response.details?.includes(
        "Cookies are not enabled in current environment."
      )
    ) {
      setHideGoogleButton(true);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const params = {
        _provider: "google",
        _token: {
          accessToken: tokenResponse.access_token,
        },
      };
      handleSocialLogin(params);

      // Display Current Salary Dialog after login
      if (!store.getState().user.isDropCV) {
        const dontShow = localStorage.getItem(
          `currentSalaryDialogDontShow_${user.id}`
        );
        if (user && !user.currentSalary && !dontShow) {
          dispatch(updateCurrentSalaryDialog(true));
          props.handleClose();
        }
      }
    },
    onError: (errorResponse) => onFailure(errorResponse),
  });

  // Password toggle handler
  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  // Function to trigger job seekers who log in via email
  const triggerCustomEventEmailLogin = () => {
    sendTrackingEvent({ event: "email-login" });
  };

  // Function to trigger job seekers who log in via SSO
  const triggerCustomEventSsoLogin = (provider) => {
    sendTrackingEvent({
      event: `${provider == "google" ? "google-login" : "facebook-login"}`,
    });
  };

  // Function to trigger job seekers who sign up for the first time via SSO
  const triggerCustomEventSsoFirstTimeSignup = (provider) => {
    if (signUpOrigin === "job_recommendation" && fypTab === 1) {
      if (signUpOrigin === "jobAlert") {
        sendTrackingEvent({
          event: "CE_sign_up_job_alert",
          method: provider === "google" ? "google" : "facebook",
          origin: "job_alert",
        });
      } else {
        sendTrackingEvent({
          event: "CE_sign_up_rec",
          method: provider === "google" ? "google" : "facebook",
          origin: "job_recommendation",
        });
      }
    } else {
      sendTrackingEvent({
        event: `${provider == "google"
          ? "google-sso-first-time-signup"
          : "facebook-sso-first-time-signup"
          }`,
      });
    }
    posthogSignup(
      selectedJob,
      selectedJob?.company,
      signUpOrigin,
      provider === "google" ? "google" : "facebook"
    );
  };

  const [values, setValues] = useState({
    id: "123456789",
    email:
      router.query.hiredlyx || router.query.login === "true"
        ? router.query.email || ""
        : "",
    password: "",
    error: "",
    loading: false,
    message: "",
  });

  const { id, email, password, loading, message, error } = values;

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (email == "" || password == "") {
      const cParams = {
        message: "Please key in both email and password to log in",
        severity: "error",
      };
      triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);

      return;
    }

    let params = {
      canCancel: true,
      email: email,
      password: password,
    };

    dispatch(globalLoginFunc(params)).then(async (response) => {
      let userId = response?.payload?.user?.id;
      let user = response?.payload?.user;
      let cvDrop = response?.payload?.cvDrop ? true : false;

      if (response.type === types.NEW_SESSION_LOGIN_SUCCEED) {
        setValues({
          ...values,
          email: "",
          password: "",
        });
        props.handleClose();
        Cookies.set("userId", userId, { expires: 365 }); // Store user ID to cookies to be used by GA with 1 year expiry
        Cookies.set("isRegistered", userId ? true : false, { expires: 365 }); // Check if user is registered/logged in and store cookie for CleverTap

        triggerCustomEventEmailLogin();

        // check if visitor job alert exists and store to local storage
        if (store.getState().jobs.visitorJobAlert) {
          setItem(
            "visitorJobAlert",
            store.getState().jobs.visitorJobAlert.toString()
          );
        }

        localStorage.setItem("job_preference_clicked", 0);

        const emailHotJob = getItem("email-hot-job");

        if (emailHotJob) {
          localStorage.removeItem("email-hot-job");
          return router.push("/profile#for-you");
        }

        if (
          response.payload.user.resume === "" &&
          !response.payload.user.onboardingComplete
        ) {
          props.handleClose();
        }

        if (fromDropResume) {
          sendTrackingEvent({
            event: "CE_login_drop_resume",
            method: "email",
            origin: "drop_resume_landing_page",
          });
          if (s3Url) {
            try {
              const response = await fetch(s3Url);
              // Convert promise response to blob
              const blob = await response.blob();
              const file = new File([blob], s3Url.split("/").pop(), {
                type: blob.type,
              });

              let urlAndEnvParams = new URLSearchParams(window.location.search);

              // If user from ad campaign, send param to backend (?utm_medium=&utm_source=)
              if (urlAndEnvParams?.size > 0) {
                var fullUrl = window.location.href; // Get the full URL
              }

              convertBase64(file).then((cvFile) => {
                // Trigger graphQL to upload cvFile
                let params = {
                  resume: cvFile,
                  resume_drop: true,
                  canCancel: false,
                  user_resume: "",
                  origin: "drop_resume_page",
                  first_time_upload_with_resume: cvDrop, // To determine if the user has already uploaded resume on onboarding/profile page and decide to submit resume via drop resume page
                  bio: "", // To indicate if user signed up for resume-review and/or via ad campaign
                  autoGenerated: false,
                  isLoggedIn: true,
                  cUser: user,
                  media_source: fullUrl,
                  cv_drop: true,
                };
                props.newResumeUpload(params).then((response) => {
                  if (response.type === types.UPDATE_USER_CV_SUCCEED) {
                    let cParams = {
                      message: "Updated and dropped resume successfully",
                      severity: "success",
                    };
                    triggerSnackBar(
                      props.hideSnackBar,
                      props.showSnackBar,
                      cParams
                    );

                    Cookies.remove("s3fileUrl", {
                      path: "/",
                    });
                    Cookies.remove("s3fileName", {
                      path: "/",
                    });

                    // Drop Resume Login - Display Current Salary Dialog
                    const dontShow = user?.id
                      ? localStorage.getItem(
                        `currentSalaryDialogDontShow_${user.id}`
                      )
                      : null;
                    if (user && !user.currentSalary && !dontShow) {
                      localStorage.setItem(
                        "showCurrentSalaryDialogAfterJump",
                        "1"
                      );
                    }

                    router.push("/jobs");
                    return;
                  } else {
                    toast.error(
                      response.payload?.message
                        ? response.payload.message
                        : "Failed to update resume, please try again later"
                    );
                    return;
                  }
                });
              });
            } catch (error) {
              console.error("Error processing S3 file:", error);
              toast.error("Failed to process the stored resume file");
              return;
            }
            return;
          }
        }
        // Current Salary Dialog
        const dontShow = localStorage.getItem(
          `currentSalaryDialogDontShow_${userId}`
        );
        if (
          response.payload.user?.id &&
          !response.payload.user.currentSalary &&
          !dontShow
        ) {
          dispatch(updateCurrentSalaryDialog(true));
        } else {
          router.reload();
        }
      } else if (
        response.type === types.SESSION_LOGIN_FAILED &&
        response.invalid
      ) {
        let cParams = {
          message: "Invalid email or password.",
          severity: "error",
        };
        triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
      } else if (
        response.type === types.SESSION_LOGIN_FAILED_INVALID_EMAIL &&
        response.invalid
      ) {
        props.handleClickSignUp(true);
      } else {
        let cParams = {
          message: "Failed to login at the moment, please try again later.",
          severity: "error",
        };
        triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
      }
      updateJobAlertVisitorEmailDialog(false);
    });
  };

  const handleChange = (name) => (e) => {
    setValues({ ...values, error: false, [name]: e.target.value });
  };

  const getProvider = (ssoLogin) => {
    switch (ssoLogin) {
      case "Continue with Google": {
        return "google";
      }
      case "Continue with Linkedin": {
        return "facebook";
      }
      case "Continue with Facebook": {
        return "facebook";
      }
    }
  };

  const handleSocialLogin = (response) => {
    let provider = response._provider;

    // To track users who sign up from an advertisement
    let urlParams = document.cookie
      .split("; ")
      .find((row) => row.startsWith("params="))
      ?.toString()
      .replace("params=", "");

    const currentUrl = router.asPath;
    const { signupMedium, signupSource } = signupTracking(
      currentUrl,
      signUpOrigin || "",
      selectedJob || {},
      {},
      true, // isSocialLogin = true
      provider // socialProvider = "google", "linkedin", etc.
    );

    let params = {
      provider: provider,
      accessToken: response._token.accessToken,
      urlParams: urlParams,
      signupMedium: signupMedium,
      signupSource: signupSource,
    };

    props.socialMediaLogin(params).then((response) => {
      try {
        if (socialButtonRefs[provider]) {
          socialButtonRefs[provider].props.triggerLogout();
        }
      } catch (error) {
        //
      }

      if (
        response.type === types.SOCIAL_LOGIN_FAILED &&
        response.payload?.noEmail === true
      ) {
        let cParams = {
          message: "Social login failed, please try again later.",
          severity: "error",
        };
        triggerSnackBar(props.hideSnackBar, props.showSnackBar, cParams);
      }

      if (response.type === types.SOCIAL_LOGIN_SUCCEED) {
        let userId = response.payload.user.id;
        let userName = response.payload.user.name;
        let userEmail = response.payload.user.email;
        const user = response.payload.user;

        if (user.pdpaAccepted === false) {
          sessionStorage.setItem("pdpa_dialog_user_id", String(user.id));

          store.dispatch({
            type: types.SET_PDPA_DIALOG_REQUESTED,
            payload: true,
          });
        }

        // Check if visitor job alert exists and store to local storage
        if (store.getState().jobs.visitorJobAlert) {
          setItem(
            "visitorJobAlert",
            store.getState().jobs.visitorJobAlert.toString()
          );
        }

        Cookies.set("userId", userId, { expires: 365 }); // Store user ID to cookies to be used by GA with 1 year expiry
        Cookies.set("isRegistered", userId ? true : false, { expires: 365 }); // Check if user is registered/logged in and store cookie for CleverTap

        localStorage.setItem("job_preference_clicked", 0);

        if (fromDropResume) {
          sendTrackingEvent({
            event: "CE_login_drop_resume_landing_page",
            method: provider === "google" ? "google" : "facebook",
            origin: "drop_resume_landing_page",
          });
        }

        if (store.getState().user.isDropCV) {
          if (response.payload && response.payload.newUser) {
            // Google enhanced conversion tracking for sign ups on drop resume page
            sendTrackingEvent({
              event: "event_enhanced_conversion",
              enhanced_conversion_data: {
                email: `${userEmail}`,
                phone_number: ``,
                last_name: ``,
                first_name: `${userName}`,
              },
            });

            triggerCustomEventSsoFirstTimeSignup(provider);
          }
          // Drop Resume Login - Display Current Salary Dialog
          const dontShow = localStorage.getItem(
            `currentSalaryDialogDontShow_${user.id}`
          );
          if (user && !user.currentSalary && !dontShow) {
            dispatch(updateCurrentSalaryDialog(true));
            props.handleClose();
          } else {
            router.reload();
          }
          // router.reload();
        } else if (response.payload && response.payload.newUser) {
          // Google enhanced conversion tracking for SSO sign ups
          sendTrackingEvent({
            event: "event_enhanced_conversion",
            enhanced_conversion_data: {
              email: `${userEmail}`,
              phone_number: ``,
              last_name: ``,
              first_name: `${userName}`,
            },
          });

          // Delay pushing GTM events to ensure CleverTap script has been initiated first
          triggerCustomEventSsoFirstTimeSignup(provider);
          props.handleClose();
        } else {
          triggerCustomEventSsoLogin(provider);
          if (
            response.payload.user.resume === "" &&
            !response.payload.user.onboardingComplete
          ) {
            props.handleClose();
            return dispatch(updateResumeConfirmationDialog(true));
          }

          const emailHotJob = getItem("email-hot-job");

          if (emailHotJob) {
            localStorage.removeItem("email-hot-job");
            return router.push("/profile#for-you");
          }

          // Display current salary dialog
          const dontShow = localStorage.getItem(
            `currentSalaryDialogDontShow_${user.id}`
          );
          if (user && !user.currentSalary && !dontShow) {
            dispatch(updateCurrentSalaryDialog(true));
            return;
          } else {
            router.reload();
          }
          // router.reload();
        }
      }
      updateJobAlertVisitorEmailDialog(false);
    });
  };

  const handleSocialLoginFailure = (error) => { };

  const handleLinkedInButtonClick = () => {
    const context = {
      currentUrl: router.asPath,
      signUpOrigin: signUpOrigin,
      selectedJob: selectedJob,
      timestamp: Date.now(),
    };
    localStorage.setItem("linkedin_context", JSON.stringify(context));

    let oauthUrl = new URL("https://www.linkedin.com/oauth/v2/authorization");
    oauthUrl.searchParams.append("response_type", "code");
    oauthUrl.searchParams.append("client_id", process.env.LINKEDIN_ID);
    oauthUrl.searchParams.append(
      "redirect_uri",
      process.env.LINKEDIN_REDIRECT_URI
    );
    oauthUrl.searchParams.append("scope", process.env.LINKEDIN_SCOPE);

    var width = 450,
      height = 730,
      left = window.screen.width / 2 - width / 2,
      top = window.screen.height / 2 - height / 2;

    window.location = oauthUrl;

    if (fromDropResume) {
      sendTrackingEvent({
        event: "CE_login_drop_resume_landing_page",
        method: "linkedin",
        origin: "drop_resume_landing_page",
      });
    }

    // check if visitor job alert exists and store to local storage
    if (store.getState().jobs.visitorJobAlert) {
      setItem(
        "visitorJobAlert",
        store.getState().jobs.visitorJobAlert.toString()
      );
    }
    updateJobAlertVisitorEmailDialog(false);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit(null);
    }
  };

  const { t } = useTranslation();

  return (
    <Fragment>
      <DialogContentStyled>
        <FormTitle>{t("sign_in_form.login")}</FormTitle>
        <EmailLoginFormContainer>
          <form>
            <Grid container justifyContent="center">
              <EmailContainer item>
                <TextInputFunction
                  loggingIn={loggingIn}
                  label={"Email"}
                  type="email"
                  email={email}
                  handleChange={handleChange("email")}
                  onKeyDown={onKeyDown}
                  passwordShown={passwordShown}
                />
              </EmailContainer>
              <PasswordContainer item>
                <TextInputFunction
                  loggingIn={loggingIn}
                  label={"Password"}
                  type={passwordShown ? "text" : "password"}
                  password={password}
                  handleChange={handleChange("password")}
                  onKeyDown={onKeyDown}
                  passwordShown={passwordShown}
                  togglePassword={togglePassword}
                />
              </PasswordContainer>
            </Grid>
          </form>
        </EmailLoginFormContainer>
        <SubmissionContainer>
          <ForgotPasswordContainer>
            <ActionLabelStyled
              onClick={handleOpenForgotPassword}
              color="primary"
            >
              {t("sign_in_form.forgot_password")}
            </ActionLabelStyled>
          </ForgotPasswordContainer>
          <LogInButton
            onClick={handleSubmit}
            data-testid="login-submit-btn"
            variant="contained"
            disabled={loggingIn}
          >
            {t("sign_in_form.login")} {fromDropResume && s3Url ? "To Explore Jobs" : ""}
          </LogInButton>
          <SignUpContainer>
            <LabelStyled $caption>{t("sign_in_form.dont_have_an_account")}</LabelStyled>
            <ActionLabelStyled onClick={handleClickSignUp} color="primary">
              {t("sign_in_form.signup")}
            </ActionLabelStyled>
          </SignUpContainer>
        </SubmissionContainer>
        <Fragment>
          <CustomDividerContainer>
            <CustomDivider />
            <LabelDivider>{t("sign_in_form.or_continue_with")}</LabelDivider>
            <CustomDivider />
          </CustomDividerContainer>
          <SSOLoginContainer item>
            {props.socialLoggingIn || loggingIn ? (
              <SocialSpinnerContainer>
                <CircularProgressStyled />
              </SocialSpinnerContainer>
            ) : (
              <Fragment>
                <GoogleButton
                  onClick={googleLogin}
                  $hide_button={hideGoogleButton ? "true" : "false"}
                  data-testid="login-google-btn"
                >
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/google-logo-login.svg"
                    }
                    alt="google"
                  />
                </GoogleButton>
                <LinkedInButton onClick={handleLinkedInButtonClick} data-testid="login-linkedin-btn">
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/linkedin-logo-login.svg"
                    }
                    alt="linkedin"
                  />
                </LinkedInButton>
                <SocialLoginButton
                  getInstancez={(node) => {
                    if (node) {
                      if (
                        socialButtonRefs[
                        getProvider("Continue with Facebook")
                        ] == undefined
                      ) {
                        socialButtonRefs[
                          getProvider("Continue with Facebook")
                        ] = node;
                      }
                    }
                  }}
                  provider={getProvider("Continue with Facebook")}
                  appId={getAppId("Continue with Facebook")}
                  onLoginSuccess={handleSocialLogin}
                  onLoginFailure={handleSocialLoginFailure}
                  dataTestId="login-facebook-btn"
                // onLogoutSuccess={handleSocialLogout}
                // onLogoutFailure={handleSocialLogout}
                >
                  <img
                    src={
                      config.assetDomain +
                      "/images/hiredly/facebook-logo-login.svg"
                    }
                    alt="facebook"
                  />
                </SocialLoginButton>
              </Fragment>
            )}
          </SSOLoginContainer>
        </Fragment>
      </DialogContentStyled>
    </Fragment>
  );
}

function Login(props) {
  const {
    open,
    handleClickSignUp,
    handleClickSignIn,
    handleClose,
    signUp,
    forgotPassword,
    handleOpenForgotPassword,
    disableClose,
  } = props;

  return (
    <LoginDialog
      open={open}
      onClose={(event, reason) => {
        if (disableClose && (reason === "backdropClick" || reason === "escapeKeyDown")) {
          return;
        }

        handleClose();
      }}
      aria-labelledby="form-dialog-title"
    >
      {open ? (
        <LazyGoogleOAuthProvider>
          <HeaderDialog>
            <HiredlyLogoGrid>
              <LogoStyled
                src={config.assetDomain + "/images/landing/hiredly-logo.png"}
                alt="hiredly-logo"
                layout="fill"
              />
            </HiredlyLogoGrid>
            {!disableClose && (
              <CloseIconContainer onClick={handleClose}>
                <CloseIcon />
              </CloseIconContainer>
            )}
          </HeaderDialog>

          {forgotPassword ? (
            <ForgotPassword handleClose={handleClose} />
          ) : !signUp ? (
            <LoginForm
              handleClickSignUp={handleClickSignUp}
              login={props.login}
              loggingIn={props.loggingIn}
              socialMediaLogin={props.socialMediaLogin}
              hideSnackBar={props.hideSnackBar}
              showSnackBar={props.showSnackBar}
              handleOpenForgotPassword={handleOpenForgotPassword}
              handleClose={handleClose}
              socialLoggingIn={props.socialLoggingIn}
              signInText={props.signInText}
              updateJobAlertVisitorEmailDialog={
                props.updateJobAlertVisitorEmailDialog
              }
              newResumeUpload={props.newResumeUpload}
            />
          ) : (
            signUp && (
              <SignUp
                handleClose={handleClose}
                handleClickSignIn={handleClickSignIn}
              />
            )
          )}
        </LazyGoogleOAuthProvider>
      ) : null}
    </LoginDialog>
  );
}

const mapStateToProps = (state) => {
  return {
    loggingIn: state.user.loggingIn,
    socialLoggingIn: state.user.socialLoggingIn,
    signInText: state.navbar.signInText,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    socialMediaLogin: bindActionCreators(socialMediaLogin, dispatch),
    hideSnackBar: bindActionCreators(hideSnackBar, dispatch),
    showSnackBar: bindActionCreators(showSnackBar, dispatch),
    updateJobAlertVisitorEmailDialog: bindActionCreators(
      updateJobAlertVisitorEmailDialog,
      dispatch
    ),
    newResumeUpload: bindActionCreators(newResumeUpload, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
